import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Accordion, AccordionHeader, AccordionItem, AccordionPanel, AvatarGroup, AvatarGroupItem, Body1, Body1Strong, Button, Caption1, Card, CardFooter, CardHeader, CardPreview, Dialog, DialogBody, DialogContent, DialogSurface, DialogTitle, DialogTrigger, makeStyles, Persona, Popover, PopoverSurface, PopoverTrigger, PresenceBadge, Spinner, Tab, TabList, tokens, Tooltip, } from "@fluentui/react-components";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { ShellServiceIdentity } from "./shellService";
import { AppsAddInRegular, ArrowDownloadRegular, BranchForkRegular, BugRegular, DeleteRegular, DismissRegular, LinkRegular, MailRegular, PeopleCommunityRegular, } from "@fluentui/react-icons";
import { Fade } from "@fluentui/react-motion-components-preview";
import { Logger } from "core/Misc/logger";
import { Link } from "shared-ui-components/fluent/primitives/link";
import { useExtensionManager } from "../contexts/extensionManagerContext";
const useStyles = makeStyles({
    extensionButton: {},
    extensionsDialogSurface: {
        height: "auto",
        width: "70vw",
        maxWidth: "600px",
        maxHeight: "70vh",
        backgroundColor: tokens.colorNeutralBackground2,
    },
    extensionDialogBody: {
        maxWidth: "100%",
        maxHeight: "100%",
    },
    extensionDialogContent: {
        marginLeft: `calc(-1 * ${tokens.spacingHorizontalM})`,
        marginRight: `calc(-1 * ${tokens.spacingHorizontalS})`,
    },
    extensionHeader: {},
    extensionItem: {},
    extensionCardPreview: {
        padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalM}`,
        display: "flex",
        flexDirection: "column",
        rowGap: tokens.spacingVerticalL,
    },
    extensionIntro: {
        display: "flex",
        alignItems: "center",
        columnGap: tokens.spacingHorizontalM,
    },
    extensionDescription: {
        display: "flex",
        flexDirection: "row",
        columnGap: tokens.spacingHorizontalS,
    },
    extensionButtonContainer: {
        marginLeft: "auto",
        alignSelf: "flex-start",
    },
    spinner: {
        animationDuration: "1s",
        animationName: {
            from: { opacity: 0 },
            to: { opacity: 1 },
        },
    },
    webResourceDiv: {
        display: "flex",
        flexDirection: "column",
    },
    webResourceLink: {
        display: "flex",
        flexDirection: "row",
        columnGap: tokens.spacingHorizontalS,
        alignItems: "center",
    },
    personPopoverSurfaceDiv: {
        display: "flex",
        flexDirection: "column",
        rowGap: tokens.spacingVerticalS,
    },
    accordionHeaderDiv: {
        display: "flex",
        flexDirection: "row",
        columnGap: tokens.spacingHorizontalS,
        alignItems: "center",
    },
    resourceDetailsDiv: {
        display: "flex",
        flexDirection: "column",
        rowGap: tokens.spacingVerticalS,
    },
    peopleDetailsDiv: {
        display: "flex",
        flexDirection: "row",
        columnGap: tokens.spacingHorizontalXL,
    },
    avatarGroupItem: {
        cursor: "pointer",
    },
});
function AsPersonMetadata(person) {
    if (typeof person === "string") {
        return { name: person };
    }
    return person;
}
function GetAvatarImage(person) {
    return person.avatar ? { src: `data:image/png;base64,${person.avatar}` } : undefined;
}
function usePeopleMetadata(people) {
    const definedPeople = useMemo(() => (people ? people.filter((person) => !!person) : []), [people]);
    return useMemo(() => definedPeople.map(AsPersonMetadata), [definedPeople]);
}
const WebResource = (props) => {
    const { url, urlDisplay, icon, label } = props;
    const classes = useStyles();
    return (_jsx("div", { className: classes.webResourceDiv, children: _jsx(Tooltip, { content: label, relationship: "label", positioning: "before", withArrow: true, children: _jsxs("div", { className: classes.webResourceLink, children: [icon, _jsx(Link, { url: url, value: urlDisplay || url })] }) }) }));
};
const PersonDetailsPopover = (props) => {
    const { person, title, disabled, children } = props;
    const classes = useStyles();
    if (disabled) {
        return _jsx(_Fragment, { children: children });
    }
    return (_jsxs(Popover, { withArrow: true, children: [_jsx(PopoverTrigger, { disableButtonEnhancement: true, children: children }), _jsx(PopoverSurface, { children: _jsxs("div", { className: classes.personPopoverSurfaceDiv, children: [_jsx(Persona, { name: person.name, secondaryText: title, avatar: { image: GetAvatarImage(person) } }), person.email && _jsx(WebResource, { url: `mailto:${person.email}`, urlDisplay: person.email, icon: _jsx(MailRegular, {}), label: "Email" }), person.url && _jsx(WebResource, { url: person.url, urlDisplay: person.url, icon: _jsx(LinkRegular, {}), label: "Website" }), person.forumUserName && (_jsx(WebResource, { url: `https://forum.babylonjs.com/u/${person.forumUserName}`, urlDisplay: person.forumUserName, icon: _jsx(PeopleCommunityRegular, {}), label: "Forum" }))] }) })] }));
};
const ExtensionDetails = memo((props) => {
    const { extension } = props;
    const { metadata } = extension;
    const classes = useStyles();
    const [canInstall, setCanInstall] = useState(false);
    const [canUninstall, setCanUninstall] = useState(false);
    const [isStateChanging, setIsStateChanging] = useState(false);
    useEffect(() => {
        const updateState = () => {
            setCanInstall(!extension.isInstalled && !extension.isStateChanging);
            setCanUninstall(extension.isInstalled && !extension.isStateChanging);
            setIsStateChanging(extension.isStateChanging);
        };
        const stateChangedHandlerRegistration = extension.addStateChangedHandler(updateState);
        updateState();
        return stateChangedHandlerRegistration.dispose;
    }, [extension]);
    const [author] = usePeopleMetadata(useMemo(() => [metadata.author], [metadata.author]));
    const contributors = usePeopleMetadata(metadata.contributors);
    const hasResourceDetails = metadata.homepage || metadata.repository || metadata.bugs;
    const hasPeopleDetails = author || contributors.length > 0;
    const hasPreviewDetails = hasResourceDetails || hasPeopleDetails;
    const hasAuthorDetails = author?.email || author?.url || author?.forumUserName;
    const subHeader = [metadata.version ? `${metadata.version}` : null, metadata.license ? `${metadata.license}` : null].filter(Boolean).join(" | ");
    const install = useCallback(async () => {
        try {
            await extension.installAsync();
        }
        catch {
            // Ignore errors. Other parts of the infrastructure handle them and communicate them to the user.
        }
    }, [extension]);
    const uninstall = useCallback(async () => {
        try {
            await extension.uninstallAsync();
        }
        catch {
            // Ignore errors. Other parts of the infrastructure handle them and communicate them to the user.
        }
    }, [extension]);
    return (_jsxs(AccordionItem, { className: classes.extensionItem, value: extension.metadata.name, children: [_jsx(AccordionHeader, { className: classes.extensionHeader, expandIconPosition: "end", children: _jsxs("div", { className: classes.accordionHeaderDiv, children: [_jsx(Body1Strong, { children: extension.metadata.name }), _jsx(Fade, { visible: extension.isInstalled, children: _jsx(PresenceBadge, { size: "small" }) })] }) }), _jsx(AccordionPanel, { children: _jsxs(Card, { children: [_jsx(CardHeader, { header: _jsx(Body1, { children: metadata.description }), description: _jsx(Caption1, { italic: true, children: subHeader }) }), hasPreviewDetails && (_jsxs(CardPreview, { className: classes.extensionCardPreview, children: [hasResourceDetails && (_jsxs("div", { className: classes.resourceDetailsDiv, style: { display: "flex" }, children: [metadata.homepage && _jsx(WebResource, { url: metadata.homepage, icon: _jsx(LinkRegular, {}), label: "Website" }), metadata.repository && _jsx(WebResource, { url: metadata.repository, icon: _jsx(BranchForkRegular, {}), label: "Repository" }), metadata.bugs && _jsx(WebResource, { url: metadata.bugs, icon: _jsx(BugRegular, {}), label: "Report Issues" })] })), hasPeopleDetails && (_jsxs("div", { className: classes.peopleDetailsDiv, style: { display: "flex" }, children: [author && (_jsx(PersonDetailsPopover, { person: author, title: "Author", disabled: !hasAuthorDetails, children: _jsx(Persona, { name: author.name, secondaryText: "Author", avatar: { image: GetAvatarImage(author) }, style: { cursor: hasAuthorDetails ? "pointer" : "default" } }) })), contributors.length > 0 && (_jsx(AvatarGroup, { layout: "stack", children: contributors.map((contributor) => {
                                                return (_jsx(PersonDetailsPopover, { person: contributor, title: "Contributor", children: _jsx(AvatarGroupItem, { name: contributor.name, className: classes.avatarGroupItem, image: GetAvatarImage(contributor) }) }, contributor.name));
                                            }) }))] }))] })), _jsxs(CardFooter, { children: [canInstall && (_jsx(Button, { appearance: "primary", size: "small", icon: _jsx(ArrowDownloadRegular, {}), onClick: install, children: "Get" })), canUninstall && (_jsx(Button, { appearance: "secondary", size: "small", icon: _jsx(DeleteRegular, {}), onClick: uninstall, children: "Remove" })), isStateChanging && _jsx(Spinner, { className: classes.spinner, size: "extra-small" })] })] }) })] }));
});
export const ExtensionListServiceDefinition = {
    friendlyName: "ExtensionList",
    consumes: [ShellServiceIdentity],
    factory: (shellService) => {
        const registration = shellService.addToolbarItem({
            key: "ExtensionList",
            horizontalLocation: "right",
            verticalLocation: "top",
            teachingMoment: {
                title: "Extensions",
                description: "Extensions provide new optional features that can be useful to your specific task or workflow. Click this button to manage extensions.",
            },
            order: -200,
            component: () => {
                const classes = useStyles();
                const [selectedTab, setSelectedTab] = useState("available");
                const extensionManager = useExtensionManager();
                const [extensions, setExtensions] = useState([]);
                useEffect(() => {
                    if (extensionManager) {
                        const populateExtensionsAsync = async () => {
                            const query = await extensionManager.queryExtensionsAsync(undefined, undefined, selectedTab === "installed");
                            const extensions = await query.getExtensionsAsync(0, query.totalCount);
                            setExtensions(extensions);
                        };
                        // eslint-disable-next-line github/no-then
                        populateExtensionsAsync().catch((error) => {
                            Logger.Warn(`Failed to populate extensions: ${error}`);
                        });
                    }
                }, [extensionManager, selectedTab]);
                return (_jsxs(Dialog, { children: [_jsx(DialogTrigger, { disableButtonEnhancement: true, children: _jsx(Tooltip, { content: "Manage Extensions", relationship: "label", children: _jsx(Button, { className: classes.extensionButton, appearance: "subtle", icon: _jsx(AppsAddInRegular, {}) }) }) }), _jsx(DialogSurface, { className: classes.extensionsDialogSurface, children: _jsxs(DialogBody, { className: classes.extensionDialogBody, children: [_jsx(DialogTitle, { action: _jsx(DialogTrigger, { action: "close", children: _jsx(Button, { appearance: "subtle", "aria-label": "close", icon: _jsx(DismissRegular, {}) }) }), children: _jsxs(_Fragment, { children: ["Extensions", _jsxs(TabList, { className: classes.extensionDialogContent, selectedValue: selectedTab, onTabSelect: (event, data) => {
                                                        setSelectedTab(data.value);
                                                    }, children: [_jsx(Tab, { value: "available", children: "Available" }), _jsx(Tab, { value: "installed", children: "Installed" })] })] }) }), _jsx(DialogContent, { className: classes.extensionDialogContent, children: _jsx(Accordion, { collapsible: true, children: extensions.map((extension) => (_jsx(ExtensionDetails, { extension: extension }, extension.metadata.name))) }) })] }) })] }));
            },
        });
        return {
            dispose: () => registration.dispose(),
        };
    },
};
//# sourceMappingURL=extensionsListService.js.map